/**
 * A checklist of a group's recent transactions, to put some of them in a
 * project — shared by New project and a project's own Add transactions.
 *
 * Each row says which project it is in now, because ticking one of those
 * *moves* it: a transaction is in at most one project, and a move nobody could
 * see coming is the one thing this list must not do. A row locked in an
 * archived project is shown, greyed, rather than hidden, so its absence from
 * the result is not a mystery. `pickerRows` in `src/lib/projects.ts` decides
 * all of that; this only draws it.
 */

import { useId } from 'react'
import { describeMoves, type PickerRow } from '../lib/projects'
import { formatDay, formatMoney } from '../lib/format'

export function TransactionPicker({
  legend,
  rows,
  ticked,
  onChange,
  disabled = false,
}: {
  legend: string
  rows: readonly PickerRow[]
  ticked: ReadonlySet<string>
  onChange: (next: Set<string>) => void
  disabled?: boolean
}) {
  const uid = useId()
  const moves = describeMoves(rows, ticked)

  const toggle = (id: string, on: boolean) => {
    const next = new Set(ticked)
    if (on) next.add(id)
    else next.delete(id)
    onChange(next)
  }

  return (
    <fieldset className="form__row picker">
      <legend>{legend}</legend>
      {rows.length === 0 && <p className="muted note">There is nothing here to add.</p>}
      <ul className="picker__list">
        {rows.map(({ transaction, locked, note, current }) => {
          const id = `${uid}-${transaction.id}`
          return (
            <li key={transaction.id} className={`picker__row${locked ? ' picker__row--locked' : ''}`}>
              <input
                id={id}
                type="checkbox"
                checked={!locked && ticked.has(transaction.id)}
                disabled={disabled || locked}
                onChange={(event) => toggle(transaction.id, event.target.checked)}
              />
              <label htmlFor={id} className="picker__what">
                <span className="picker__description">
                  {transaction.description}
                  {current && <span className="pill pill--project">{current.name}</span>}
                </span>
                <span className="picker__when">
                  {formatDay(transaction.occurredOn)} ·{' '}
                  {formatMoney(transaction.amountCents, transaction.currency)}
                </span>
                {note && <span className="picker__note">{note}</span>}
              </label>
            </li>
          )
        })}
      </ul>
      {moves && <p className="muted note">{moves}</p>}
    </fieldset>
  )
}
